/* eslint-disable @typescript-eslint/no-explicit-any */
import TextField from '@jobseeker/lotus/elements/Input';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Badge,
  Card,
  Fade,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Skeleton,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useScrollTrigger,
  useTheme,
} from '@mui/material';
import {ArrowBackIos, ArrowForwardIos} from '@mui/icons-material';
import kebabCase from 'lodash/kebabCase';
import moment from 'moment';
import {Image, ImageProps} from '@jobseeker/lotus/components/Image';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {
  CATEGORY,
  COMPANY,
  ENGINEERS_WEEK,
  EXPERIENCE_MAPPING,
  JOBAPPLIED,
  JOBDETAIL,
  JOBFEED,
  LOCATION_BASED,
  mapExpToBucket,
  PopularTags,
  RECRUITER_JOBFEED,
  SEARCH,
  TAG,
  WOMEN_AT_WORK,
} from '@jobseeker/common-core/constants';
import {
  CrossOutlined,
  DefaultJobLogo,
  DifferentlyAbled,
  Home,
  IImjobsProfileUpdateInfoIcon,
  Medal,
  ProfileUpdateInfoIcon,
  SearchIconGrey,
  SurveyBackground,
  Women,
  WomenProfessional,
  Survey as SurveyImg,
} from '@jobseeker/lotus/assets/icons';
import CTA from '@jobseeker/lotus/components/CTA';
import FilterChips from '@jobseeker/lotus/components/FilterChips';
import FilterDialog from '@jobseeker/lotus/components/FilterDialog';
import InfiniteScroll from '@jobseeker/lotus/components/InfiniteScroll';
import JobList from '@jobseeker/lotus/components/JobList';
import Loader from '@jobseeker/lotus/components/Loader';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {Grid} from '@jobseeker/lotus/foundations';
import {Box} from '@mui/material';

// import {NoJobFound} from '@jobseeker/lotus/assets/images';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import {deleteCookie, setCookie} from 'cookies-next';

import type {AppState, Job, ProfileTags} from '@jobseeker/common-core/types';
import type {JobFeedPropsType} from '../types';
import {searchJobsbyQuery} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import ExpiredJob from '@jobseeker/feature-job/components/ExpiredJob';
import NoJobsFound from '@jobseeker/feature-job/components/NoJobsFound';
import SeoInterlinking from '@jobseeker/feature-job/components/SeoInterlinking';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import AdsPanelHarness from '@jobseeker/client-harness/components/AdsHarness/AdsPanelV2';
import {
  jobDetailService,
  jobfeedService,
} from '@jobseeker/common-core/services';
import {
  createPopularTagUrl,
  getExperienceRange,
  getExpFilter,
  getItemWithExpiry,
  getKeyFromPath,
  getPrefText,
  getQueryParamsAndSeekerId,
  getUbaPageName,
  handleTracking,
  openUrl,
  setExpFilter,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {FreeTextSearchBar} from '@jobseeker/lotus/components';
import Survey from '@jobseeker/feature-ads/components/Survey';
import {getKeywordSearch} from '@jobseeker/feature-search/helpers';
import {calculateAppliedFilters} from '@jobseeker/lotus/components/FilterChips/utils';
import JobSearchBar from '@jobseeker/lotus/components/JobSearchBar';
import {
  CheckBox,
  CityPageLisiting,
  DownAppBanner,
  HiristLoader,
  HiristSeoLinking,
} from '@jobseeker/lotus/elements';
import {Checkbox} from '@mui/material';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {getRandomColor} from '@jobseeker/lotus/theme';
import {cleanUrl, getOldLink} from '@jobseeker/lotus/utils';
import {useRouter} from 'next/router';
import Sticky from 'react-stickynode';
import {getWindow} from 'ssr-window';

const window = getWindow();

const Icon = (props: any) => (
  <Image
    src={props.icon}
    alt={props.label ?? 'Icon Alt Tag'}
    width={16}
    height={16}
    objectFit="cover"
    quality={50}
  />
);

const Listbox = styled('div')<any>(({theme, variant}: any) => ({
  width: '230px',
  zIndex: 10,
  position: 'sticky',
  listStyle: 'none',
  boxShadow: '0 13px 14px 0 rgba(0,0,0,.13)',
  // padding: '8px 0',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#000',
  overflow: 'auto',
  maxHeight: 650,
  // border: '1px solid',
  marginLeft: -8,
  // borderColor:
  //   variant === VersionVariants.VERSION_ONE ? '#bbb' : theme.palette.divider,
  borderRadius: variant === VersionVariants.VERSION_ONE ? 6 : 16,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  marginTop: 4,
  '& li.Mui-focused': {
    backgroundColor: '#4a8df6',
    color: 'white',
    cursor: 'pointer',
  },
  '& li:active': {
    backgroundColor: '#2977f5',
    color: 'white',
  },
  [theme.breakpoints.down('sm')]: {
    width: '220px', // Example: Change width for small screens
    marginTop: 0, // Example: Adjust zIndex on mobile
    marginLeft: '16px',
  },
}));

const List = styled('li')(() => ({
  padding: '12px 16px',
  fontSize: '16px',
  ':hover': {
    backgroundColor: '#F2F5FA',
  },
}));
export const additionalInfoIconMap: any = {
  femaleCandidate: <Icon icon={Women} />,
  femaleBackWorkForce: <Icon icon={WomenProfessional} />,
  differentlyAbled: <Icon icon={DifferentlyAbled} />,
  exDefence: <Icon icon={Medal} />,
  workFromHome: <Icon icon={Home} />,
};
interface ImageWithFallbackProps extends ImageProps {
  fallback: any;
  itemId: any;
}
export const ImageWithFallback = ({
  fallback,
  alt,
  src,
  ...props
}: ImageWithFallbackProps) => {
  const [error, setError] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setError(false);
  }, [src]);

  const randomColor = useMemo(() => getRandomColor(props.itemId), [src]);

  return (
    <>
      {!error ? (
        <Image
          key={alt}
          src={src}
          alt={alt}
          loading="lazy"
          onErrorCapture={() => setError(true)}
          style={{
            border: '0.6px solid #7E7E7E33',
            backgroundColor: '#fff',
            objectFit: 'cover',
            marginLeft: '-25px',
            borderRadius: '8px',
          }}
          {...props}
        />
      ) : (
        <Box
          key={`${alt}_${randomColor}`}
          className="joblist__logo"
          sx={{
            background: randomColor,
            p: theme.spacing(1.5),
            height: 'min-content',
            lineHeight: 0,
            marginLeft: '-25px',
            border: '0.6px solid #7E7E7E33',
            borderRadius: '8px',
          }}
        >
          <Image
            src={DefaultJobLogo}
            alt={'Logo'}
            width="25"
            height={'25'}
            objectFit="cover"
            quality={50}
          />
        </Box>
      )}
    </>
  );
};

export const StickyContainer = ({children, page}: any) => {
  const isHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

  const isJobfeed = page === 'jobfeed';
  const isLarge = useIsLargeDevice();

  if (isHirist && isLarge) {
    return (
      <Sticky
        enabled={isLarge}
        top={
          page === WOMEN_AT_WORK
            ? 153
            : page === ENGINEERS_WEEK
            ? 155
            : 154 + 94 - (isJobfeed ? 34 : 0)
        }
        innerZ={9}
        enableTransforms={false}
      >
        {children}
      </Sticky>
    );
  } else {
    return <>{children}</>;
  }
};
const TooltipHirist = styled(({className, ...props}: TooltipProps) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  ul: {
    margin: 0,
    padding: '14px 15px',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: '#1c2d41',
    color: '#fff',
    fontSize: 16,
    // border: '1px solid #dadde9',
  },
  '.MuiTooltip-arrow:before': {
    backgroundColor: '#1c2d41',
  },
}));

const JobFilterComponent = ({
  onFilter,
  title,
  page,
  onClearFilter,
  onFilterChange,
  showFilter,
  showBadge,
  preFilters,
  setLoader,
}: any) => {
  const isLarge = useIsLargeDevice();
  const jobFeedStore = useSelector((state: AppState) => state.job);
  const searchFormValue = useSelector((state: AppState) => state.job.filters);
  const filterChips = useSelector((state: AppState) => state.job.filterChips);
  const industryOptionsState = useSelector(
    (state: AppState) => state.job.industryOptions
  );
  const dispatch = useDispatch();
  const theme = useTheme();
  const [keywordSearch, setKeywordSearch] = useState<string[]>([]);
  const [autocomplete, setAutocomplete] = useState<any>(false);
  const [hasPreFilter, setHasPreFilters] = useState<boolean>(false);

  const router = useRouter();
  const pathname = router.asPath;
  const isAISearchRoute = pathname.startsWith('/ai-search');
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const setSearchFormValue = (value: any) => {
    dispatch({
      type: 'job/jobFeed/set_filters',
      payload: {filters: value, options: {showSort: false}},
    });
  };

  const appliedFilters = useMemo(() => {
    const industryOptions: any = [
      ...(industryOptionsState?.trendingIndustries || []),
      ...(industryOptionsState?.industries || []),
    ];

    const calcAppliedFilter = calculateAppliedFilters(
      searchFormValue,
      industryOptions.length
        ? {
            industry: industryOptions,
          }
        : {}
    );
    return calcAppliedFilter;
  }, [searchFormValue, industryOptionsState]);

  const onSearchFormChange = (e: any, value: any) => {
    let formv: any = {};
    setHasPreFilters(false);
    const _value = value || e.target.value;
    try {
      formv[e.target.name] = _value;
    } catch (error) {
      if (value === 'dialog') {
        formv = e;
      }
    }
    const newV = {...searchFormValue, ...formv};
    setSearchFormValue(newV);

    if (onFilterChange) onFilterChange(newV);

    if (page === 'search' && e.target.name === 'query') {
      if (_value.length > 1) {
        getKeywordSearch(_value).then(searchInfo => {
          setKeywordSearch(searchInfo);
        });
        setAutocomplete(true);
      }
    }
  };

  const onFilterSubmit = useCallback(
    (jobData: any) => {
      dispatch({
        type: 'job/jobFeed/set_filter_chips',
        payload: {chips: appliedFilters},
      });

      const expFilters = appliedFilters.filter(
        (f: any) => f.key === 'experience'
      );
      if (expFilters.length) {
        const range = getExperienceRange(expFilters[0].value);
        setExpFilter(page, range);
      }

      if (onFilter) {
        onFilter(jobData);
      }
    },
    [appliedFilters, dispatch, onFilter]
  );

  // const clearFilter = () => {
  //   if (onClearFilter) {
  //     onClearFilter();
  //   }
  //   setSearchFormValue({});
  // };

  const deleteFilter = (filterValue: any) => {
    dispatch({
      type: 'job/jobFeed/remove_filter_chip',
      payload: {chipLabel: filterValue?.label},
    });

    let filteredFormValue = searchFormValue[filterValue.key]?.filter(
      (fv: any) => fv != filterValue.value
    );
    if (filterValue.selected) {
      filteredFormValue = filteredFormValue?.filter(
        (fv: any) => !filterValue.selected.includes(fv)
      );
    }
    const newFormValue = {...searchFormValue};
    newFormValue[filterValue.key] = filteredFormValue;
    setSearchFormValue(newFormValue);
    if (filterValue.key === 'experience') {
      if (
        process.env.NEXT_PUBLIC_FILTER_CAT_TAG === 'true' &&
        isAppIimjobs &&
        (page === CATEGORY || page === TAG)
      ) {
        localStorage.removeItem('filterCat');
        deleteCookie('filterCat');
      } else {
        localStorage.removeItem('filter');
        deleteCookie('filter');
      }
    }
    if (onFilter) {
      onFilter(newFormValue, true);
    }
  };

  useEffect(() => {
    if (isAISearchRoute && preFilters && Object.keys(preFilters).length) {
      const industryOptions: any = [
        ...(industryOptionsState?.trendingIndustries || []),
        ...(industryOptionsState?.industries || []),
      ];

      const {minexp, maxexp, posting, loc, industry, query, ...rest} =
        preFilters as any;
      const expBucket = mapExpToBucket(minexp, maxexp);

      const normalizedForUi: any = {
        ...rest,
        experience: expBucket ? [expBucket] : null,
        posting:
          posting != null && posting !== 0 && posting !== '0'
            ? Array.isArray(posting)
              ? posting
              : [posting]
            : [0],
        loc: loc != null ? (Array.isArray(loc) ? loc : [loc]) : null,
        industry:
          industry != null
            ? Array.isArray(industry)
              ? industry
              : [industry]
            : null,
        query,
        minexp,
        maxexp,
      };
      const calcAppliedFilter = calculateAppliedFilters(
        normalizedForUi,
        industryOptions.length ? {industry: industryOptions} : {}
      );
      dispatch({
        type: 'job/jobFeed/set_filter_chips',
        payload: {chips: calcAppliedFilter},
      });
      dispatch({
        type: 'job/jobFeed/set_filters',
        payload: {
          filters: normalizedForUi,
          options: {showSort: false},
        },
      });
    }
  }, [preFilters, dispatch, industryOptionsState, isAISearchRoute]);

  useEffect(() => {
    if (isAISearchRoute) return;

    const queryParams = window.location.search;
    const filterKeys = [
      'posting',
      'loc',
      'category',
      'minexp',
      'maxexp',
      'posting',
      'industry',
    ];
    let sessionFilter = {};
    try {
      sessionFilter = getExpFilter(page);
    } catch (e) {}
    if (
      filterKeys.reduce(
        (exists, value) => exists || queryParams.includes(value),
        false
      ) ||
      Object.keys(sessionFilter).length
    ) {
      setHasPreFilters(true);
    } else {
      setHasPreFilters(false);
    }
  }, [isAISearchRoute]);

  useEffect(() => {
    if (hasPreFilter && appliedFilters) {
      dispatch({
        type: 'job/jobFeed/set_filter_chips',
        payload: {chips: appliedFilters},
      });
    }
  }, [hasPreFilter, appliedFilters]);
  const isJobfeed = page === 'jobfeed';
  const chipsToShow = filterChips?.filter(
    (item: {label: string}) =>
      ![
        'Date',
        'Relevance',
        'All Categories',
        'All Postings',
        'All Exp. Level',
        'keyword',
        'query',
      ].includes(item.label)
  );
  const isHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

  return (
    <>
      <Grid
        item
        sx={{
          ...(isHirist
            ? {display: {xxs: 'none', md: 'block'}, ml: 0}
            : {
                mt: isJobfeed ? 0.85 : 0,
                maxWidth: 'calc(100% - 70px)',
                whiteSpace: 'nowrap',
              }),
        }}
      >
        {!isAISearchRoute && (
          <Typography
            component={isHirist ? 'p' : 'h1'}
            fontSize={isHirist ? 14 : {xxs: 16, lg: 20}}
            display="flex"
            color="secondary.dark"
            sx={{
              wordWrap: 'break-word',
              textTransform: 'capitalize',
              ...(isHirist
                ? {fontWeight: 600}
                : {
                    fontWeight: {xxs: 600, lg: 500},
                    mt: isJobfeed ? 0.85 : 0,
                    maxWidth: 'calc(100% - 70px)',
                  }),
            }}
          >
            {isHirist ? 'Filter By:' : title}
          </Typography>
        )}
      </Grid>
      {showFilter && (
        <>
          {!isAISearchRoute && (
            <Grid
              item
              xs={page === 'search' ? 12 : undefined}
              md={page === 'jobfeed' ? true : undefined}
              lg={page === 'jobfeed' ? true : undefined}
              display={{
                xxs: 'none',
                md: 'flex',
              }}
              maxWidth={
                isHirist && (page === WOMEN_AT_WORK || page === ENGINEERS_WEEK)
                  ? '750px'
                  : isHirist
                  ? {
                      md: '550px',
                      xl: page === 'location_based' ? '250px' : '550px',
                    }
                  : isJobfeed || page === 'search' || page === WOMEN_AT_WORK
                  ? 'calc(100% - 70px)'
                  : {
                      md: page === 'location_based' ? '400px' : '450px',
                      xl: page === 'location_based' ? 'auto' : '550px',
                    }
              }
              sx={{
                width: {
                  md: page === WOMEN_AT_WORK ? '70%' : '100%',
                  xl: page === WOMEN_AT_WORK ? '60%' : '100%',
                },
              }}
            >
              <JobSearchBar
                variant={
                  isHirist
                    ? VersionVariants.VERSION_HIRIST
                    : VersionVariants.VERSION_TWO
                }
                showSortBy={false}
                hideIndustryFilter={page === 'jobfeed'}
                hideLocationBar={page === 'location_based' ? true : false}
                isFilter={page !== 'search' || isHirist}
                showSearchInput={
                  page === 'search' && !isHirist && !isAISearchRoute
                }
                formValue={searchFormValue}
                onChange={onSearchFormChange}
                showCategory={page === WOMEN_AT_WORK}
                showFunctionalArea={page === ENGINEERS_WEEK}
                industryOptions={[
                  {
                    options: industryOptionsState?.trendingIndustries,
                    label: 'Trending Industries',
                    value: 2,
                  },
                  {
                    options: industryOptionsState?.industries,
                    label: 'Other Industries',
                    value: 1,
                  },
                ]}
                onFilter={onFilterSubmit}
                isLoading={jobFeedStore.isLoading}
                autocompleteList={keywordSearch}
                onAutoCompleteSelect={() => setAutocomplete(false)}
                showAutocomplete={autocomplete}
                onlySeachInput={false}
                showPosting={
                  (isHirist &&
                    page !== WOMEN_AT_WORK &&
                    page !== ENGINEERS_WEEK) ||
                  (page === 'search' && isLarge)
                    ? true
                    : false
                }
                showIndustry={page !== 'jobfeed' && !isHirist ? true : false}
                tooltip={
                  page === 'jobfeed' && isLarge
                    ? {
                        experience: searchFormValue.experience?.length && {
                          title: (() => {
                            // Get the first experience value from the array
                            const expValue = searchFormValue.experience[0];
                            // Convert to experience range
                            const expRange = getExperienceRange(expValue);
                            return isHirist
                              ? 'Jobs are tailored to match your experience level. You can adjust your experience range using this filter'
                              : `You're currently seeing jobs for ${expRange.minexp} to ${expRange.maxexp} years of experience. You can adjust your experience range using this filter!`;
                          })(),
                          persist: true,
                        },
                        industry: {
                          title: (
                            <Typography fontWeight={500} fontSize={'11px'}>
                              Explore jobs with the new <i>“Industry Filter”</i>
                            </Typography>
                          ),
                        },
                      }
                    : {
                        industry: {
                          title: (
                            <Typography fontWeight={500} fontSize={'11px'}>
                              Explore jobs with the new <i>“Industry Filter”</i>
                            </Typography>
                          ),
                        },
                      }
                }
              />
            </Grid>
          )}

          {isHirist && chipsToShow.length > 0 && isLarge && (
            <Grid item>
              <Typography
                color={'primary'}
                component={'p'}
                fontSize={14}
                sx={{cursor: 'pointer'}}
                onClick={() => {
                  setTimeout(() => {
                    setLoader(true);
                    onClearFilter();
                    setTimeout(() => setLoader(false), 4000);
                  }, 0);
                }}
              >
                Clear All
              </Typography>
            </Grid>
          )}

          {!isAISearchRoute && (
            <Grid item display={{xxs: 'flex', md: 'none'}}>
              <Badge
                overlap="circular"
                color={isHirist ? 'success' : 'error'}
                badgeContent=" "
                invisible={!showBadge}
                sx={{
                  margin: theme.spacing(0.875),
                  '& .MuiBadge-badge': {
                    color: theme.palette.error.dark,
                    width: theme.spacing(1),
                    height: theme.spacing(1),
                    padding: 0,
                    minWidth: theme.spacing(1),
                    top: isHirist ? '20%' : '10%',
                    right: isHirist ? '28%' : '24%',
                  },
                }}
              >
                <FilterDialog
                  variant={VersionVariants.VERSION_TWO}
                  onFilter={onFilterSubmit ?? console.error}
                  isFilter={page !== 'search'}
                  onChange={data => onSearchFormChange(data, 'dialog')}
                  formValue={searchFormValue}
                  showCategory={page === WOMEN_AT_WORK}
                  showSortBy={false}
                  showPosting={
                    (isHirist &&
                      page !== WOMEN_AT_WORK &&
                      page !== ENGINEERS_WEEK) ||
                    page === 'search'
                      ? true
                      : false
                  }
                  isLoading={jobFeedStore.isLoading}
                  showFunctionalArea={page === ENGINEERS_WEEK}
                  industryOptions={
                    isHirist
                      ? undefined
                      : [
                          {
                            options:
                              jobFeedStore.industryOptions?.trendingIndustries,
                            label: 'Trending Industries',
                            value: 2,
                          },
                          {
                            options: jobFeedStore.industryOptions?.industries,
                            label: 'Other Industries',
                            value: 1,
                          },
                        ]
                  }
                  showIndustry={page !== 'jobfeed'}
                />
              </Badge>
            </Grid>
          )}

          {!isHirist && !isAISearchRoute && page === 'search' && (
            <Grid item xxs={12} display={{xxs: 'flex', md: 'none'}}>
              <JobSearchBar
                variant={
                  isHirist
                    ? VersionVariants.VERSION_HIRIST
                    : VersionVariants.VERSION_TWO
                }
                showSortBy={false}
                isFilter={false}
                onlySeachInput={isAISearchRoute ? false : true}
                showSearchInput={isAISearchRoute ? false : true}
                formValue={searchFormValue}
                onChange={onSearchFormChange}
                onFilter={onFilter}
                isLoading={jobFeedStore.isLoading}
                autocompleteList={keywordSearch}
                onAutoCompleteSelect={() => setAutocomplete(false)}
                showAutocomplete={autocomplete}
                showPosting={isLarge ? true : false}
                showIndustry={page === 'jobfeed' && !isHirist ? true : false}
              />
            </Grid>
          )}
          {!isHirist && isLarge && chipsToShow.length > 0 && (
            <Grid item xxs={12} width={'100%'}>
              <FilterChips
                items={chipsToShow}
                onDelete={deleteFilter}
                onClear={onClearFilter}
              />
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export const JobFeed = ({
  onLoadMore,
  onInfiniteScroll,
  onJobSave,
  onUndoActionClick,
  onJobActionClick,
  onFilter,
  onClearFilter,
  feedOnly,
  learnGrow,
  title,
  jobFeedStore,
  jobInfoKeyElements,
  routeToJobDetail,
  infiniteScroll,
  onFilterChange,
  showSurvey,
  surveyQuestions,
  setShowSurvey,
  currQuestion,
  setCurrentQuestion,
  showMarketingSurvey,
  marketingSurveyQuestions,
  setShowMarketingSurvey,
  marketingCurrQuestion,
  setMarketingCurrQuestion,
  marketingSurveyPositions,
  page,
  injectAsChild = false,
  showFilter = true,
  showApplied = false,
  checkJobs,
  preFilters,
  extractedParams,
  catId,
  platinumPartners,
}: JobFeedPropsType) => {
  const [loadMoreLoading, setLoadMoreLoading] = useState<boolean>(false);
  const searchFormValue = useSelector((state: AppState) => state.job.filters);
  const [loader, setLoader] = useState<boolean>(false);
  const profile = useSelector(
    (state: AppState) => state.user.profile.profileUpdateCohorts,
    shallowEqual
  );
  const [showProfileUpdateBanner, setShowProfileUpdateBanner] =
    useState<boolean>(false);
  const router = useRouter();
  const pathname = router.asPath;
  const isAISearchRoute = pathname.startsWith('/ai-search');
  const rawQuery = router?.query?.query;
  const searchQuery =
    typeof rawQuery === 'string' ? rawQuery.split('-').join(' ') : '';
  const [freeTextInput, setFreeTextInput] = useState<string>(searchQuery || '');

  useEffect(() => {
    if (
      profile?.perfectMatch === true ||
      profile?.perfectMatchStrict === true
    ) {
      setShowProfileUpdateBanner(true);
    }
  }, [profile]);
  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory
  );
  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 700,
  });
  const hideBanner = getItemWithExpiry('HIDE_APP_PROMO_BANNER');

  const userData = useSelector((state: AppState) => state.user, shallowEqual);

  const {redirectedFromExpiredJD = false} = useSelector(
    (state: AppState) => state.app
  );

  const {
    profile: {
      experienceYear,
      gender,
      tags,
      functionalArea,
      email,
      locations,
      name,
      experienceStatus,
    },
    completeness: {profileStatus},
  } = useSelector((state: AppState) => state.user, shallowEqual);

  const ContainerComponent = infiniteScroll ? InfiniteScroll : Box;
  let learnNGrowAdIndex = 0;

  const onLoadFeed = () => {
    setLoadMoreLoading(true);
    if (onLoadMore) {
      if (!injectAsChild) {
        onLoadMore();
      }
    }
    setTimeout(() => {
      setLoadMoreLoading(false);
    }, 1000);
  };
  const {isLoading, isFilterApplied} = useSelector(
    (state: AppState) => state.job
  );

  const onJobClick = (job: Job, jobPos: number) => {
    TrackEventUBA('jobClick', 'click', {
      ref: new URL(window.location.href).searchParams.get('ref') || '',
      categoryID: page == 'category' ? categoryId : undefined,
      tagID: page == 'tag' ? Number(tagId) : undefined,
      jobID: job.id,
      jobRef: getKeyFromPath('ref') || '',
      jobPos,
      jobVersion: '',
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
    // const slug = kebabCase(job.title.replace(/&/g, '-and-'));
    // routeToJobDetail(slug, job.id);
  };

  /** Validate whether Ads already loaded due to SSR*/
  let learnGrowInfo = useSelector(
    (state: AppState) => state.ads.learnGrow,
    shallowEqual
  );

  if (!learnGrowInfo[0]?.title) {
    const [learnGrowList, setLearnGrow] = useState([]);
    useLoadAds(
      learnGrow?.slot || '',
      setLearnGrow,
      {
        exp: experienceYear ? experienceYear.toString() : '',
        gender: gender === 'female' ? '0,2' : '0,1',
        functionalArea: functionalArea ? `0,${functionalArea.id || ''}` : '0,',
        categoryIds: categoryId ? categoryId?.toString() : '',
        tags:
          tags && tags?.length
            ? tags?.map((tag: ProfileTags) => tag?.tagId).join(',')
            : '',
        logged: email ? 2 : 1,
      },
      false,
      !learnGrowInfo[0]?.title
    );
    learnGrowInfo = learnGrowList;
  }
  const handleUbaTracking = (id?: string, title?: string) => {
    TrackEventUBA('feedNativeClick', 'click', {
      categoryID: page === 'category' ? categoryId : undefined,
      nativeID: id && id.length > 0 ? id : learnGrowInfo || '',
      nativeTitle: title && title.length > 0 ? title : '',
      tagID: page === 'tag' ? Number(tagId) : undefined,
      Source: page || '',
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
  };

  const dispatch = useDispatch();
  const clearFilter = () => {
    dispatch({
      type: 'job/jobFeed/set_filters',
      payload: {
        filters: {query: searchFormValue.query ?? ''},
        options: {showSort: false, clearFilter: true},
      },
    });
    if (onClearFilter) {
      onClearFilter();
    }
  };
  const filterApplied = useMemo(() => {
    let value = false;
    for (const key in searchFormValue) {
      if (
        !!searchFormValue[key] &&
        !['page', 'sort'].includes(key) &&
        searchFormValue[key]?.length &&
        !searchFormValue[key].includes(0)
      ) {
        value = true;
        break;
      }
    }
    if (page === 'search' && !value) return true;
    return value;
  }, [searchFormValue]);

  const showBadge = useMemo(() => {
    let value = false;
    for (const key in searchFormValue) {
      if (
        !!searchFormValue[key] &&
        !['page', 'sort', 'query'].includes(key) &&
        searchFormValue[key]?.length &&
        !searchFormValue[key].includes(0)
      ) {
        value = true;
        break;
      }
    }
    return value;
  }, [searchFormValue]);
  const isJobfeed = page === 'jobfeed';
  const [hasPreFilter, setHasPreFilters] = useState<boolean>(false);
  const industryOptionsState = useSelector(
    (state: AppState) => state.job.industryOptions
  );

  const setSearchFormValue = (value: any) => {
    dispatch({
      type: 'job/jobFeed/set_filters',
      payload: {filters: value, options: {showSort: false}},
    });
  };
  const onSearchFormChange = (e: any, value: any) => {
    let formv: any = {};
    setHasPreFilters(false);
    const _value = value || e.target.value;
    try {
      formv[e.target.name] = _value;
    } catch (error) {
      if (value === 'dialog') {
        formv = e;
      }
    }
    const newV = {...searchFormValue, ...formv};
    setSearchFormValue(newV);

    if (onFilterChange) onFilterChange(newV);

    if (page === 'search' && e.target.name === 'query') {
      if (_value.length > 1) {
        getKeywordSearch(_value).then(searchInfo => {
          setKeywordSearch(searchInfo);
        });
        setAutocomplete(true);
      }
    }
  };
  const [keywordSearch, setKeywordSearch] = useState<string[]>([]);
  const [autocomplete, setAutocomplete] = useState<any>(false);
  const {showAutocomplete, onAutoCompleteSelect, autocompleteList} = {
    showAutocomplete: autocomplete,
    onAutoCompleteSelect: () => setAutocomplete(false),
    autocompleteList: keywordSearch,
  };
  const isHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

  const WrapperCard: any = isHirist ? Box : Card;

  const [checkedJobs, setCheckedJobs] = useState<
    Map<string, string | undefined>
  >(() => new Map());

  const addItem = (jobId: string, refCode?: string) => {
    setCheckedJobs(prev => new Map(prev).set(jobId, refCode));
  };
  const [showTooltip, setShowTooltip] = useState(false);
  const [showSpacerBox, setShowSpacerBox] = useState(true);

  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggeredOnce = useRef(false);

  useEffect(() => {
    const seen = localStorage.getItem('multiApplyTooltipShown');
    if (seen) {
      setShowTooltip(false);
      setShowSpacerBox(false);
      return;
    }

    setShowTooltip(true);
    localStorage.setItem('multiApplyTooltipShown', 'true');

    const handleScroll = () => {
      if (!triggeredOnce.current && window.scrollY > 200) {
        setShowTooltip(false);
        triggeredOnce.current = true;
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const removeItem = (jobId: string) => {
    setCheckedJobs(prev => {
      const next = new Map(prev);
      next.delete(jobId);
      return next;
    });
  };

  const onCheckClick = (jobId: string, refCode?: string) => {
    setTimeout(() => {
      if (checkedJobs.has(jobId)) {
        removeItem(jobId);
      } else {
        addItem(jobId, refCode);
      }
    }, 0);
  };
  const [navigationWidth, setNavigationWidth] = useState(0);

  const cancelApplyJobs = () => {
    setTimeout(() => {
      setCheckedJobs(new Map());
    }, 0);
  };
  const [load, setLoad] = useState<boolean>(false);
  const [hover, setHover] = useState(false);
  const getBackgroundColor = (hover: any) =>
    hover ? '#e9630c' : 'transparent';

  const validateJobApplicationRedirections = (refInfo = 'jd') => {
    const redirectPath =
      typeof window !== 'undefined' ? window.location.href : '/jobfeed';

    // Incomplete Profile Cases
    let profileEdit = '';
    let baseUrl = '';

    if (!profileStatus?.personalDetails) {
      profileEdit = `${profileEdit}&pd=pd`;
      baseUrl = `/registration/addPersonalDetails?profiletype=1${profileEdit}&has=1`;
    }

    if (!profileStatus?.educationDetails && !baseUrl) {
      profileEdit = `${profileEdit}&ed=ed`;
      baseUrl = `/registration/addEducationalDetails?profiletype=1${profileEdit}&has=1`;
    }

    if (!profileStatus?.resumeAttached && !baseUrl) {
      profileEdit = `${profileEdit}&ur=ur`;
      baseUrl = `/registration/addPersonalDetails?profiletype=1${profileEdit}&has=1`;
    }

    if (
      !profileStatus?.professionalDetails &&
      !(experienceStatus === 2) &&
      !baseUrl
    ) {
      profileEdit = `${profileEdit}&pro=pro`;
      baseUrl = `/registration/addWorkExperience?profiletype=1${profileEdit}`;
    }

    if (baseUrl) {
      baseUrl = `${baseUrl}&redirectto=${redirectPath}`;
    }

    return baseUrl;
  };

  const applyMultipleJobs = async () => {
    setTimeout(async () => {
      setLoad(true);

      try {
        if (!isLoggedIn) {
          // Store job IDs and refCodes in localStorage
          // Merge checkJobs and checkedJobs Maps
          const allJobsMap = new Map(checkedJobs);
          if (checkJobs) {
            checkJobs.forEach((refCode, id) => {
              if (!allJobsMap.has(id)) {
                allJobsMap.set(id, refCode);
              }
            });
          }

          const jobsData = Array.from(allJobsMap.entries()).map(
            ([id, refCode]) => ({
              id,
              refCode,
            })
          );
          localStorage.setItem(
            'applyJobIdsPostLogIn',
            JSON.stringify(jobsData)
          );

          dispatch({
            type: 'AppContext/set_login_popup',
            payload: true,
          });
          return;
        }

        // Combine checkJobs and checkedJobs
        const allJobsMap = new Map(checkedJobs);
        if (checkJobs) {
          checkJobs.forEach((refCode, id) => {
            if (!allJobsMap.has(id)) {
              allJobsMap.set(id, refCode);
            }
          });
        }

        const jobIds = Array.from(allJobsMap.keys());
        const refCodes = Array.from(allJobsMap.values());

        const redirectUrl = validateJobApplicationRedirections();
        if (redirectUrl?.length > 0) {
          openUrl(redirectUrl);
          return;
        }

        await jobDetailService.applyToMultipleJobs(
          jobIds,
          {
            coverLetter: userData.profile.coverLetterId,
            jobId: '',
          },
          refCodes
        );

        const jobsAppliedCount = jobIds.length;

        setCheckedJobs(new Map());
        openUrl(
          `${
            process.env.NEXT_PUBLIC_LOAD_CONFIG
          }/job/applied?jobId=${jobIds.join(
            ','
          )}&ref=multiapp&count=${jobsAppliedCount}&pref=jf`
        );

        if (page === 'jobdetail' || page === 'jobapplied') {
          const jobFeeds = await jobfeedService.getSimilarJobs({
            userId: userData.profile.id,
            id: jobIds[0],
            cat: catId,
            tags:
              tags && tags?.length
                ? tags?.map((tag: ProfileTags) => tag?.tagId)
                : [],
            exp: experienceYear ? experienceYear.toString() : '',
            size: 100,
            ...(process.env.NEXT_PUBLIC_APP_NAME === 'hirist' && {
              whatFor: 'hirist',
            }),
          });

          dispatch({
            type: 'job/jobFeed/set_jobfeed',
            payload: jobFeeds,
          });

          dispatch({
            type: 'job/jobFeed/set_loadMore',
            payload: false,
          });
        } else if (page === 'jobfeed') {
          window.location.reload();
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoad(false);
      }
    }, 0);
  };

  const {
    profile: {subscriptions},
  } = useSelector((state: AppState) => state.user);
  const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);

  const showPremium =
    !(
      subscriptions?.earlyApplicant?.isValid ||
      subscriptions?.resumeBuilder?.isValid
    ) &&
    isLoggedIn &&
    isHirist;

  useEffect(() => {
    const updateNavigationWidth = () => {
      const widthNavigationTag = document.querySelector(
        '#navigation_listing_tabs'
      );
      if (widthNavigationTag) {
        setNavigationWidth(widthNavigationTag.clientWidth);
      }
    };

    updateNavigationWidth();
    window.addEventListener('resize', updateNavigationWidth);
    return () => window.removeEventListener('resize', updateNavigationWidth);
  }, []);

  const isLarge = useIsLargeDevice();
  const theme = useTheme();
  const [menuOpen, setOpen] = useState<any>(false);

  const setMenuOpen = (value: any) => {
    // console.log(v, menuOpen);
    if (value && value === menuOpen) {
      setMenuOpen(false);
    } else setOpen(value);
  };
  const textClick = (e: any) => {
    setMenuOpen(false);
    // textBarRef.current.focus();
  };
  const onlySpecialCharRegex = /^[^a-zA-Z0-9]+$/;
  const interceptor: any = useInterceptor();

  const {
    ads: {courseJobList},
  } = interceptor;

  const [courses, setCourses] = useState<any[]>([]);
  const [tooltipShown, setTooltipShown] = useState(false);
  useEffect(() => {
    const handleStorageUpdate = () => {
      const shown = !!localStorage.getItem('experience-tooltip-shown');
      setTooltipShown(shown);
    };
    let scrollTimeout: any = null;

    window.addEventListener('scroll', () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        setTooltipShown(true);
      }, 200);
    });
    window.addEventListener('experience-tooltip-updated', handleStorageUpdate);

    return () => {
      window.removeEventListener(
        'experience-tooltip-updated',
        handleStorageUpdate
      );
    };
  }, []);

  const courseSlotId =
    courseJobList &&
    courseJobList.length > 0 &&
    catId &&
    parseInt(String(catId))
      ? courseJobList?.[0]?.slot
      : courseJobList?.[1]?.slot;

  courseSlotId &&
    useLoadAds(courseSlotId, setCourses, {
      gender: `0`,
      functionalArea: `0,${functionalArea?.id || ''}`,
      exp: `${experienceYear || ''}`,
      categoryIds: catId && parseInt(String(catId)) ? catId : '',
      tags: catId ? '' : tags?.map(t => t.tagId)?.join(','),
      location: `${locations?.current.id || ''}`,
    });

  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const isInputDisabled =
    onlySpecialCharRegex.test(searchFormValue?.query) ||
    !searchFormValue?.query?.trim();
  const onAutoCompleteListClick = (value: string) => {
    if (onAutoCompleteSelect) {
      onAutoCompleteSelect();
      onFilterChanges({target: {name: 'query', value}}, value);
      setAutocomplete(false);
      const url = new URL(window.location.href);
      const currentQuery = url.search;

      // Construct new path
      const newUrl = `/search/${value
        .replace(/[^a-zA-Z0-9]/g, ' ')
        .trim()
        .replace(/\s+/g, '-')}${currentQuery}`;
      window.location.href = newUrl;
      // onFilter && onFilter(searchFormValue);
    }
  };
  const onFilterChanges = (e: any, value?: any) => {
    onSearchFormChange(e, value);
    // onFilter && onFilter(searchFormValue)
  };

  const handleSearchUBA = (
    userQuery?: string,
    section?: string,
    filtersOverride?: any
  ) => {
    const activeFilters = filtersOverride || searchFormValue;

    const normalizeFilter = (value: any): string => {
      if (Array.isArray(value)) {
        return value.join(',');
      }
      return value || '';
    };

    const filterExp: object = EXPERIENCE_MAPPING[
      parseInt(activeFilters?.experience) - 1
    ] ?? {
      minexp: 0,
      maxexp: 0,
    };
    TrackEventUBA('searchPageResultClick', 'click', {
      section: section || '',
      actionType: 'click',
      ref: new URL(window.location.href).searchParams.get('ref') || '',
      page: 'SearchResultPage',
      keyword: activeFilters?.query,
      actualSearch: userQuery || '',
      searchType: isAISearchRoute ? 'GenAI' : 'Normal',
      modeSelection: (() => {
        const aiSearchActive = localStorage.getItem('aiSearchActive') == 'true';
        const isChangeToggle = localStorage.getItem('changeToggle') == 'true';

        if (isChangeToggle) {
          localStorage.removeItem('changeToggle');
          return aiSearchActive ? 'Change_AI' : 'Change_Normal';
        }
        return isAISearchRoute ? 'Default_AI' : 'Default_Normal';
      })(),
      filterJobPosting: normalizeFilter(activeFilters?.posting),
      filterExp: filterExp || '',
      filterLocation: normalizeFilter(activeFilters?.loc),
      filterIndustry: normalizeFilter(activeFilters?.industry),
      otherFilters: {
        clusters: normalizeFilter(activeFilters?.clusterId),
        subClusters: normalizeFilter(activeFilters?.subClusterId),
      },
    });
  };

  const onFreeTextSearchSubmit = async (query: string) => {
    const cleanedQuery = query.trim();
    if (!cleanedQuery) return;
    const formattedQuery = query.trim().replace(/\s+/g, '-');

    dispatch({
      type: 'job/jobFeed/set_initial_state',
    });

    try {
      const apiResponse = await jobfeedService.getFreeTextSearchParams(
        cleanedQuery
      );
      const extractedParams = apiResponse.extracted_parameters || {};
      const extractedQuery = apiResponse?.extracted_query || '';

      const loc = (extractedParams.locations || []).map(
        (loc: {id: number; name: string}) => loc.id
      );
      const industry = (extractedParams.industries || []).map(
        (ind: {id: number; name: string}) => `${ind.id}`
      );
      const posting = extractedParams.jobPosted?.id ?? 0;
      const minexp = extractedParams.experience?.min ?? 0;
      const maxexp = extractedParams.experience?.max ?? 30;
      const clusterId = extractedParams?.clusters?.map(
        (cluster: {id: number}) => cluster?.id
      );

      const expBucket = mapExpToBucket(minexp, maxexp);

      const normalizedForUi: any = {
        page: 0,
        query: extractedQuery,
        loc: loc?.length ? loc : null,
        industry: industry?.length ? industry : null,
        posting: [posting || 0],
        ...(expBucket ? {experience: [expBucket]} : {}),
        minexp,
        maxexp,
        clusterId,
      };

      const industryOptions: any = [
        ...(industryOptionsState?.trendingIndustries || []),
        ...(industryOptionsState?.industries || []),
      ];
      const chips = calculateAppliedFilters(
        normalizedForUi,
        industryOptions.length ? {industry: industryOptions} : {}
      );

      router.replace(
        {
          pathname: router.pathname,
          query: {query: formattedQuery},
        },
        undefined,
        {shallow: true}
      );
      dispatch({
        type: 'job/jobFeed/set_filter_chips',
        payload: {chips},
      });

      dispatch({
        type: 'job/jobFeed/set_filters',
        payload: {filters: normalizedForUi, options: {showSort: false}},
      });

      if (onFilter) {
        await onFilter(normalizedForUi);
      }
      handleSearchUBA(query, 'Search Button', normalizedForUi);
    } catch (err) {
      console.error('Error fetching free text search results', err);
    }
  };

  return (
    <WrapperCard
      sx={
        isHirist
          ? {mr: 0}
          : {
              boxShadow: 2,
              borderRadius: 4,
              minHeight: isAISearchRoute ? '40vh' : 'auto',
            }
      }
    >
      {!feedOnly && !(page === 'category_location_based') && (
        <StickyContainer page={page}>
          <Grid
            container
            sx={{
              px: isHirist ? {xxs: 0, md: 2} : 2,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              ...(isHirist
                ? {
                    backgroundColor: {md: '#e3e9f0', xxs: 'transparent'},
                    width:
                      page === WOMEN_AT_WORK
                        ? {xxs: 'auto', md: '144%'}
                        : {xxs: 'auto', md: navigationWidth || '134%'},
                    top: isLarge
                      ? page === WOMEN_AT_WORK
                        ? '-90px'
                        : page === ENGINEERS_WEEK
                        ? '-95px'
                        : `-${99 + (isJobfeed ? 34 : 0)}px`
                      : 'unset',
                    py: {xxs: 0, md: 1.5},
                    margin: 'auto',
                    columnGap: 2,
                    justifyContent: 'start',
                    alignItems: 'center',
                    position: !isLarge ? 'fixed' : 'absolute',
                    bottom: isLarge
                      ? 'auto'
                      : trigger && !hideBanner
                      ? '104px'
                      : '0px',
                    transition: !isLarge
                      ? 'bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      : undefined,
                    right: isLarge ? 'unset' : '12px',
                    zIndex: isLarge ? 'unset' : 100,
                    mt: isJobfeed ? 4.2 : 0,
                    mb: 2,
                  }
                : {
                    mb: isJobfeed ? {xxs: 2, md: 3} : 3,
                    mt: isJobfeed ? {xxs: 2, md: 3} : 3,
                    columnGap: {xxs: 2, md: 3, lg: 4, xl: 12},
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    flexWrap: 'wrap',
                  }),
              // [theme.breakpoints.down(1200)]: {
              //   flexWrap: 'wrap',
              // },
            }}
            rowGap={filterApplied ? {xxs: 1, md: 3} : {xxs: 1, md: 3}}
            color="gray.dark"
          >
            {isAISearchRoute && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    component={'div'}
                    sx={{
                      fontSize: '16px',
                      fontWeight: '500',
                      lineHeight: '20px',
                      marginTop: '8px',
                      color: '#333333',
                      height: '20px',
                    }}
                  >
                    Search For
                  </Typography>
                  <Typography
                    component={'div'}
                    sx={{
                      fontSize: '12px',
                      fontWeight: '500',
                      lineHeight: '16px',
                      marginTop: '8px',
                      color: '#49966E',
                      height: '16px',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      localStorage.setItem('aiSearchActive', 'false');
                      handleSearchUBA(freeTextInput, 'GotoClasssicSearch');
                      dispatch({
                        type: 'job/jobFeed/set_filters',
                        payload: {
                          filters: {page: 0, concat: false},
                          options: {showSort: false},
                        },
                      });
                      dispatch({
                        type: 'AppContext/set_open_searchoverlay',
                        payload: {search: true},
                      });
                    }}
                  >
                    Back to Classic Search
                  </Typography>
                </Box>
                <Box sx={{mb: '2px'}}>
                  <FreeTextSearchBar
                    onSubmit={onFreeTextSearchSubmit}
                    useOverlay={true}
                    initialInput={searchQuery || ''}
                    onReset={handleSearchUBA}
                    onInputChange={setFreeTextInput}
                  />
                </Box>
                <JobFilterComponent
                  title={title}
                  page={page}
                  onClearFilter={clearFilter}
                  onFilter={onFilter}
                  setLoader={setLoader}
                  onFilterChange={onFilterChange}
                  showFilter={showFilter}
                  showBadge={showBadge}
                  preFilters={preFilters}
                  handleSearchUBA={handleSearchUBA}
                />
              </Box>
            )}

            {!isAISearchRoute && (
              <JobFilterComponent
                title={title}
                page={page}
                onClearFilter={clearFilter}
                onFilter={onFilter}
                setLoader={setLoader}
                onFilterChange={onFilterChange}
                showFilter={showFilter}
                showBadge={showBadge}
                preFilters={preFilters}
              />
            )}
          </Grid>
        </StickyContainer>
      )}
      <Box
        sx={{
          width: {xxs: '100%', md: '134%'},
          ml: {xxs: '8px', md: 0},
        }}
      >
        {page === ENGINEERS_WEEK && (
          <AdsPanelHarness
            variant={VersionVariants.VERSION_TWO}
            showFeatured={false}
            showLearnGrow={false}
            showFeaturedInstitute={false}
            showRolesAtLeadingBrands={false}
            showPlatinumPartners={true}
            showGoldPartners={isLarge ? false : true}
            showSmallBadges={false}
            showLearningCenterCTA={false}
            page={ENGINEERS_WEEK}
          />
        )}
      </Box>

      <Box sx={{ml: isHirist ? {xxs: 0, md: 2} : 0}}>
        {(feedOnly || isHirist) && (
          <>
            {redirectedFromExpiredJD && <ExpiredJob />}
            <Box
              display={{xxs: 'block', md: 'flex'}}
              mb={page == SEARCH && isHirist ? '16px' : 0}
              mr={page == SEARCH && isHirist ? '32px' : 0}
              justifyContent={page == SEARCH && isHirist ? 'space-between' : ''}
            >
              <Typography
                component={
                  injectAsChild
                    ? 'p'
                    : page === RECRUITER_JOBFEED
                    ? 'h2'
                    : page == COMPANY
                    ? 'h2'
                    : 'h1'
                }
                display="flex"
                fontSize={{xxs: 14, md: 20}}
                fontWeight={500}
                // m={2}
                // mt={3}
                sx={{
                  wordWrap: 'break-word',
                  textTransform: 'capitalize',
                  ...(isHirist
                    ? {
                        ml: {xxs: page === RECRUITER_JOBFEED ? 0 : 2, md: 0}, //{xxs: 2, md: 4, lg: 6},
                        mb: 1.5,
                        mt:
                          !tooltipShown && page === 'jobfeed' ? '28px' : '-6px',
                        fontWeight: page === ENGINEERS_WEEK ? 700 : 600,
                        fontSize:
                          page === ENGINEERS_WEEK
                            ? {sm: '18px', md: '22px'}
                            : '20px !important',
                      }
                    : {
                        // fontSize: {xxs: 14, md: 20},
                        m: 2,
                        mt: 3,
                      }),
                }}
              >
                {title}
              </Typography>

              {isHirist && page == SEARCH && (
                <>
                  <></>
                  <Box>
                    <TextField
                      size="small"
                      onClick={textClick}
                      placeholder="Search Jobs"
                      variant={VersionVariants.VERSION_TWO}
                      aria-autocomplete="list"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // (Optional) Prevent form submission if any

                          onAutoCompleteListClick(searchFormValue.query);
                        }
                      }}
                      aria-expanded="true"
                      autoComplete="off"
                      spellCheck="false"
                      aria-invalid="false"
                      name="query"
                      onChange={onFilterChanges}
                      error={searchFormValue?.query && isInputDisabled}
                      helperText={
                        searchFormValue?.query && isInputDisabled
                          ? 'Only special character and spaces are not allowed'
                          : ''
                      }
                      hirist={true}
                      sx={{
                        zIndex: 3,
                        fontSize: 17,
                        borderRadius: '22px',
                        width: '220px',
                        color: 'rgba(0,0,0,.4)',
                        height: '42px',
                        mt: -0.5,
                        ml: {xxs: 2, md: 0},

                        '.MuiInputBase-root': {
                          borderRadius: '22px',
                          pl: {xxs: 1.5, md: 2},
                          color: 'rgba(0,0,0,.4)',

                          height: '55px !important',
                          fontSize: 16,
                        },

                        '& .MuiOutlinedInput-root': {
                          borderRadius: '22px',
                          height: '55px !important',
                          fontSize: 17,
                          border: '1px #7E7E7E33 !important', // Remove any border
                          boxShadow: 'none !important', // Prevents shadow effect
                          '&:hover': {
                            border: '1px #7E7E7E33 !important', // Remove any border
                            boxShadow: 'none !important',
                          },
                          '&.Mui-focused': {
                            border: '1px #7E7E7E33 !important', // Remove any border
                            boxShadow: 'none !important',
                          },
                        },

                        '& .MuiInputBase-root': {
                          borderRadius: '22px',
                          pl: {xxs: 1.5, md: 2},
                          height: '55px !important',
                          fontSize: 17,
                        },

                        '& .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #7E7E7E33 !important', // Always visible border
                          display: {xxs: 'inline', md: 'none !important'},
                        },
                      }}
                      value={searchFormValue?.query}
                      InputProps={{
                        style: {
                          height: '39px',
                          backgroundColor: 'white',
                          cursor: 'default',
                        },
                        startAdornment: (
                          <InputAdornment position="start">
                            <Image
                              src={SearchIconGrey}
                              alt={'Search'}
                              width={18}
                              height={18}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {showAutocomplete &&
                      autocompleteList &&
                      autocompleteList?.length > 0 && (
                        <Listbox
                          variant={VersionVariants.VERSION_TWO}
                          sx={{
                            position: 'absolute',
                          }}
                        >
                          {autocompleteList?.map((keyword: any, index: any) => (
                            <List
                              style={{cursor: 'default'}}
                              key={`searchform-keyword-${index}`}
                              onClick={e => onAutoCompleteListClick(keyword)}
                            >
                              {keyword}
                            </List>
                          ))}
                        </Listbox>
                      )}
                  </Box>
                </>
              )}
            </Box>
            {isHirist && page == CATEGORY && (
              <>
                <Box
                  display={{
                    xs: 'none',
                    md: 'flex',
                  }}
                  marginBottom="24px"
                >
                  <Box width={200}>
                    <Typography sx={{fontSize: 14, opacity: 0.6, width: 200}}>
                      Popular Designations/Skills
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={0.5}
                    minHeight="30px"
                  >
                    {PopularTags?.find(
                      (cat: any) => cat.id == catId
                    )?.tags?.map(tag => (
                      <a
                        key={tag.id} // Always add a unique key for mapped elements
                        href={`${createPopularTagUrl(
                          tag,
                          'k'
                        )}?ref=popular_tag`}
                        onClick={e => {
                          e.preventDefault();
                          setTimeout(() => {
                            openUrl(
                              `${createPopularTagUrl(
                                tag,
                                'k'
                              )}?ref=popular_tag`,
                              '_self'
                            );
                          }, 0);
                        }}
                        style={{textDecoration: 'none'}}
                      >
                        <Button
                          sx={{
                            border: '1px solid #e9630c',
                            borderRadius: '100px',
                            padding: '11px',
                            fontSize: '14px !important',
                            paddingTop: '3px !important',
                            paddingBottom: '3px !important',

                            display: 'flex',
                            alignItems: 'center',
                            transition: 'background-color 0.3s ease-in-out',
                            '&:hover': {
                              border: '1px solid #e9630c',

                              backgroundColor: '#e9630c',
                              color: '#fff !important',
                              '& img': {
                                filter: 'brightness(0) invert(1)', // Turns image white
                              },
                              '& .text': {
                                color: '#fff', // Changes Typography color
                              },
                            },
                          }}
                        >
                          {tag.name}
                        </Button>
                      </a>
                    )) ?? []}
                  </Box>
                </Box>
              </>
            )}
          </>
        )}

        {jobFeedStore?.jobfeed.length > 0 && !isFilterApplied ? (
          <ContainerComponent
            //key={`${Date.now()}`}
            dataLength={jobFeedStore?.jobfeed?.length}
            next={!injectAsChild ? onLoadMore : console.log}
            hasMore={!!jobFeedStore.loadMoreEnabled}
            onScroll={onInfiniteScroll}
            loader={
              <Box
                sx={{
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader />
              </Box>
            }
            style={{
              scrollbarWidth: 'none',
            }}
          >
            {/* Survey at top when position is -1 */}
            {[JOBFEED, JOBAPPLIED, JOBDETAIL].includes(page) &&
              !isHirist &&
              marketingSurveyPositions?.includes(-1) &&
              showMarketingSurvey && (
                <Box
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'border.lightGray',
                  }}
                  key={`marketing-survey-box`}
                >
                  <Box
                    minHeight={117}
                    bgcolor={'prompt.background'}
                    border={'1px solid'}
                    borderColor={'prompt.main'}
                    borderRadius={5}
                    boxShadow={'0px 6.48px 6px 0px #29292914'}
                    position={'relative'}
                    overflow={'hidden'}
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'flex-start'}
                    justifyContent={'center'}
                    sx={{
                      px: {xs: 1.5, lg: 4},
                      py: 2,
                      m: 2,
                      mb: 2.875,
                    }}
                  >
                    <Box
                      position="absolute"
                      right={0}
                      top={0}
                      bottom={0}
                      margin={'auto'}
                      height={'200%'}
                    >
                      <Image
                        src={SurveyBackground}
                        width={430}
                        height={340}
                        alt=""
                      />
                    </Box>
                    {marketingCurrQuestion?.type !== 'noSelect' && (
                      <Box
                        display={isLarge ? 'flex' : 'none'}
                        position="absolute"
                        right={8}
                        top="50%"
                        margin={'auto'}
                        pt={2.5}
                        sx={{transform: 'translateY(-50%)'}}
                      >
                        <Image
                          src={SurveyImg}
                          width={216}
                          height={129}
                          alt=""
                        />
                      </Box>
                    )}
                    <Survey
                      questions={marketingSurveyQuestions}
                      setShowSurvey={setShowMarketingSurvey}
                      currQuestion={marketingCurrQuestion!}
                      setCurrentQuestion={setMarketingCurrQuestion}
                      primaryColor={'primary.main'}
                      surveyTextColor={'secondary.main'}
                      source={'JOBFEED'}
                      surveyType={'Marketing'}
                      surveyPosition={0}
                    />
                  </Box>
                </Box>
              )}
            {jobFeedStore?.jobfeed?.map((job: Job | any, index: number) => {
              const additionalInfo: any[] = [];
              jobInfoKeyElements.forEach(
                (key: {value: string; label: string}) => {
                  const {label, value} = key;
                  if (job[label]) {
                    additionalInfo.push({
                      label: value,
                      // value,
                      icon: additionalInfoIconMap[label],
                    });
                  }
                }
              );
              let adObj: any = {};
              if (index && index % 10 == 0) {
                learnNGrowAdIndex += 1;
                if (learnNGrowAdIndex >= learnGrowInfo.length) {
                  learnNGrowAdIndex = 0;
                }
                // adObj = learnGrowList[
                //   Math.floor(Math.random() * learnGrowList.length)
                // ]
                adObj = learnGrowInfo[learnNGrowAdIndex];
              }
              const courseUrl = isHirist
                ? `/course/${cleanUrl(
                    adObj?.title?.replace(/&/g, '-and-')
                  )}-${adObj?.courseId}?reforg=learningcat&refinv=nativebanner` ||
                  getOldLink(
                    `/course/${kebabCase(adObj?.title)}-${adObj?.courseId}.html`
                  ) + '?reforg=learningcat&refinv=nativebanner'
                : `${adObj?.['link']}?reforg=learningcat&refinv=nativebanner` ||
                  getOldLink(
                    `/course/${kebabCase(adObj?.title)}-${adObj?.courseId}.html`
                  ) + '?reforg=learningcat&refinv=nativebanner';

              const showSeoInterlinking = ![
                'search',
                'jobfeed',
                WOMEN_AT_WORK,
              ].includes(page)
                ? jobFeedStore?.jobfeed.length > 24
                  ? index === 24
                  : index === 6
                : false;

              if (index % 5 == 0 && index > 0 && courses?.length > 0) {
                handleTracking('lgViewNativeCourse', {
                  event_category: 'Learn and Grow',
                  Title:
                    courses[(index / 5 - 1) % courses?.length]?.title ?? '',
                  Origin: origin,
                  UserId: sessionStorage.getItem('userId') || '',
                  Device: sessionStorage.getItem('device') || 'desktop',
                  Status: sessionStorage.getItem('status') || 'LoggedOut',
                  Reforg:
                    getQueryParamsAndSeekerId(
                      courses[(index / 5 - 1) % courses.length]?.link ?? ''
                    ).queryParams['reforg'] || '',
                  RefInv:
                    getQueryParamsAndSeekerId(
                      courses[(index / 5 - 1) % courses.length]?.link ?? ''
                    ).queryParams['refinv'] || '',
                });
              }

              return (
                <Box
                  key={`${job.id}_${index}`}
                  mb={isHirist ? 2 : 0}
                  alignItems={'center'}
                >
                  {/* {isHirist &&
                    isLarge &&
                    page != LOCATION_BASED &&
                    index === 5 && (
                      <Box mb={2} pl={3}>
                        <HiristSeoLinking source={'company'} />
                      </Box>
                    )} */}
                  {page !== 'recruiterJobfeed' && isHirist && (
                    <>
                      {index == 0 && showTooltip && (
                        <Fade in={showTooltip}>
                          <Box
                            ref={tooltipRef}
                            sx={{
                              position: 'sticky',
                              mb: 1,
                              top: -40,
                              left: 0,
                              bgcolor: '#122b46',
                              color: 'white',
                              px: 1.5,
                              py: 1.5,
                              borderRadius: 1.5,
                              fontSize: 13,
                              zIndex: 5,
                              boxShadow: 3,
                              maxWidth: 350,
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -8,
                                left: 20, // tweak this to move triangle horizontally
                                width: 0,
                                height: 0,
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderTop: '8px solid #122b46',
                              },
                            }}
                          >
                            You can apply to multiple jobs with a single click.
                            Try it!
                          </Box>
                        </Fade>
                      )}
                    </>
                  )}

                  <Box
                    alignItems={isHirist ? 'flex-start' : 'center'}
                    display={'flex'}
                    height={'fit-content'}
                  >
                    {isHirist && (
                      <Box
                        component={
                          job.mediaResume ||
                          job?.assessmentFlags ||
                          job?.applyUrl?.length > 0
                            ? TooltipHirist
                            : Box
                        }
                        display={
                          job.mediaResume || job?.assessmentFlags
                            ? 'unset'
                            : 'flex'
                        }
                        arrow
                        title={
                          job.mediaResume || job?.assessmentFlags
                            ? 'The recruiter has some questions before you apply'
                            : job.applyUrl?.length > 0
                            ? 'This job requires you to apply on an external page'
                            : ''
                        }
                        height={'fit-content'}
                      >
                        {page !== 'recruiterJobfeed' && (
                          <Box
                            // ml={{xxs: 2, md: 4, lg: 6}}
                            mx={2}
                            marginTop={isHirist ? '16px' : 0}
                            display={'flex'}
                            height={'fit-content'}
                          >
                            <CheckBox
                              variant={VersionVariants.VERSION_HIRIST}
                              sx={{mx: 0, mt: 0.5}}
                              checked={checkedJobs.has(job.id.toString())}
                              disabled={
                                job.mediaResume ||
                                job?.assessmentFlags ||
                                job?.applied ||
                                job?.applyUrl?.length > 0
                              }
                              value={job.id.toString()}
                              onChange={e => {
                                onCheckClick(
                                  e.target.value,
                                  (page == JOBAPPLIED ? 'aj_' : '').concat(
                                    job.refCode
                                      ? job.refCode
                                      : page == COMPANY
                                      ? 'clp_other'
                                      : feedOnly
                                      ? 'rl'
                                      : jobFeedStore?.filters?.sort?.[0] ===
                                          'relevance' && page === 'jobfeed'
                                      ? 'reljf'
                                      : page === 'jobfeed'
                                      ? 'jf'
                                      : page === 'search'
                                      ? 'sp'
                                      : ''
                                  )
                                );
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                    <Box
                      width={'100%'}
                      marginTop={
                        isHirist && showApplied && index == 0 && job?.applied
                          ? '8px'
                          : 0
                      }
                    >
                      {' '}
                      <JobList
                        showApplied={showApplied}
                        noShadow
                        // key={`${index}${Date.now()}`}
                        title={job?.title
                          ?.split('(')[0]
                          // ?.replace(/\//gm, '/ ')
                          .trim()}
                        refCode={(page == JOBAPPLIED ? 'aj_' : '').concat(
                          job.refCode
                            ? job.refCode
                            : page == COMPANY
                            ? 'clp_other'
                            : feedOnly
                            ? 'rl'
                            : jobFeedStore?.filters?.sort?.[0] ===
                                'relevance' && page === 'jobfeed'
                            ? 'reljf'
                            : page === 'jobfeed'
                            ? 'jf'
                            : page === 'search'
                            ? 'sp'
                            : ''
                        )}
                        prefCode={getPrefText() ?? ''}
                        experiance={`${job.min} - ${job.max} yrs`}
                        companyName={job.companyData.companyName}
                        date={moment(job.createdTime).toDate()}
                        location={
                          job?.location.length > 3
                            ? 'Multiple Locations'
                            : job?.location?.map((l: any) => l.name).join('/')
                                .length > 30
                            ? 'Multiple Locations'
                            : job?.location?.map((l: any) => l.name).join('/')
                        }
                        variant={
                          process.env.NEXT_PUBLIC_APP_NAME === 'hirist'
                            ? VersionVariants.VERSION_HIRIST
                            : VersionVariants.VERSION_TWO
                        }
                        isCompanyJob={Math.random() < 0.5}
                        jobData={job}
                        jobPos={index + 1}
                        onJobClick={() => onJobClick(job, index + 1)}
                        additionalInfo={additionalInfo}
                        isPremiumJob={!!job.premium}
                        isSaved={!!job.saved}
                        onSaveClick={onJobSave}
                        onJobActionClick={onJobActionClick}
                        onUndoActionClick={onUndoActionClick}
                        companyImgUrl={
                          job.recruiter?.centralizedCompanyId &&
                          !job?.confidential
                            ? `${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}${job.recruiter?.centralizedCompanyId}.webp`
                            : ''
                        }
                        fallbackCompanyUrl={
                          job.recruiter?.centralizedCompanyId &&
                          !job?.confidential
                            ? `${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}${job.recruiter?.centralizedCompanyId}.webp`
                            : ''
                        }
                      />
                    </Box>
                  </Box>
                  {adObj?.imageUrl && (
                    <Link
                      href={courseUrl}
                      onClick={e => {
                        e.preventDefault();
                        openUrl(courseUrl, '_blank');
                        setTimeout(() => {
                          handleUbaTracking();
                        }, 0);
                      }}
                      target="_blank"
                      color={'secondary'}
                      sx={{cursor: 'pointer', textDecoration: 'none'}}
                    >
                      <CTA
                        title={
                          adObj?.title
                            ? adObj?.description
                              ? `${adObj.description} by ${adObj.title}`
                              : adObj.title
                            : adObj?.description
                        }
                        // subTitle={adObj.title}
                        imageUrl={adObj.imageUrl || adObj['Image Url']}
                        variant={VersionVariants.VERSION_ONE}
                        actionUrl={''}
                        actionLabel={adObj?.ctaText || 'View course'}
                        data-testid="jobfeed_course_banner_cta"
                      />
                    </Link>
                  )}
                  {[JOBFEED, JOBAPPLIED, JOBDETAIL].includes(page) &&
                    !isHirist &&
                    marketingSurveyPositions?.includes(index) &&
                    showMarketingSurvey && (
                      <Box
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'border.lightGray',
                        }}
                        key={`marketing-survey-box-${index}`}
                      >
                        <Box
                          minHeight={117}
                          bgcolor={'prompt.background'}
                          border={'1px solid'}
                          borderColor={'prompt.main'}
                          borderRadius={5}
                          boxShadow={'0px 6.48px 6px 0px #29292914'}
                          position={'relative'}
                          overflow={'hidden'}
                          display={'flex'}
                          flexDirection={'column'}
                          alignItems={'flex-start'}
                          justifyContent={'center'}
                          sx={{
                            px: {xs: 1.5, lg: 4},
                            py: 2,
                            m: 2,
                            mb: 2.875,
                          }}
                        >
                          <Box position="absolute" top={0} right={0} zIndex={0}>
                            <Image
                              src={SurveyBackground}
                              width={430}
                              height={340}
                              alt=""
                            />
                          </Box>

                          {marketingCurrQuestion?.type !== 'noSelect' && (
                            <Box
                              display={isLarge ? 'flex' : 'none'}
                              position="absolute"
                              right={8}
                              top="50%"
                              pt={2.5}
                              sx={{transform: 'translateY(-50%)'}}
                            >
                              <Image
                                src={SurveyImg}
                                width={216}
                                height={129}
                                alt=""
                              />
                            </Box>
                          )}
                          <Survey
                            questions={marketingSurveyQuestions}
                            setShowSurvey={setShowMarketingSurvey}
                            primaryColor={'primary.main'}
                            surveyTextColor={'secondary.main'}
                            source={
                              page === 'jobfeed' ? 'JOBFEED' : 'SIMILAR_JOBS'
                            }
                            surveyType={'Marketing'}
                            surveyPosition={0}
                            currQuestion={marketingCurrQuestion}
                            setCurrentQuestion={setMarketingCurrQuestion}
                          />
                        </Box>
                      </Box>
                    )}
                  {isJobfeed &&
                    !isHirist &&
                    index > 0 &&
                    (index + 1) % 15 === 0 &&
                    showSurvey && (
                      <Box
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'border.lightGray',
                        }}
                        key={`survey-box-${index}`}
                      >
                        <Box
                          minHeight={63}
                          bgcolor={'background.secondary'}
                          border={'1px solid'}
                          borderColor={'border.green'}
                          borderRadius={3}
                          position={'relative'}
                          overflow={'hidden'}
                          p={2}
                          px={3}
                          display={'flex'}
                          flexDirection={'column'}
                          alignItems={'flex-start'}
                          justifyContent={'center'}
                          m={2}
                        >
                          <Survey
                            questions={surveyQuestions}
                            setShowSurvey={setShowSurvey}
                            primaryColor={'primary.main'}
                            surveyTextColor={'secondary.main'}
                            source={'JF'}
                            surveyType={'RelevantJob'}
                            surveyPosition={(index + 1) / 15}
                            currQuestion={currQuestion}
                            setCurrentQuestion={setCurrentQuestion}
                          />
                        </Box>
                      </Box>
                    )}
                  {showSeoInterlinking && !isHirist && <SeoInterlinking />}
                  {index === 15 &&
                    page === 'jobfeed' &&
                    showProfileUpdateBanner && (
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        bgcolor={
                          isHirist ? 'primary.light' : 'background.secondary'
                        }
                        // borderColor={'1px solid #F6E6D1'}
                        border="1px solid"
                        borderColor="primary.main"
                        borderRadius="8px"
                        {...(isHirist
                          ? {
                              marginLeft: '72px',
                              marginRight: {xxs: 2, md: 4},
                            }
                          : {
                              margin: 2,
                            })}
                        p={2}
                        mt={2}
                      >
                        <Box display="flex" gap={1.5}>
                          <Image
                            src={
                              isHirist
                                ? ProfileUpdateInfoIcon
                                : IImjobsProfileUpdateInfoIcon
                            }
                            alt="ProfileUpdateInfoIcon"
                            height={35}
                            width={35}
                          />
                          <Box>
                            <Typography
                              fontSize="16px"
                              fontWeight={isHirist ? 700 : 600}
                              color="#333"
                              lineHeight={'28px'}
                            >
                              It’s time to review your profile
                            </Typography>
                            <Typography
                              fontSize="14px"
                              color="#6D6D6D"
                              fontWeight={isHirist ? 500 : 400}
                              lineHeight={'20px'}
                            >
                              Make sure your work experience, skills and job
                              preferences are updated.
                            </Typography>
                            <Typography
                              mt={0.5}
                              fontSize="14px"
                              color="primary.main"
                              fontWeight={isHirist ? 700 : 600}
                              lineHeight={'24px'}
                              onClick={() =>
                                openUrl('/update-profile', '_self')
                              }
                              sx={{cursor: 'pointer', display: 'inline'}}
                            >
                              Update Profile
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{cursor: 'pointer'}}
                          onClick={() => {
                            setShowProfileUpdateBanner(false);
                          }}
                        >
                          <Image
                            width={20}
                            height={20}
                            src={CrossOutlined}
                            alt="cross"
                          />
                        </Box>
                      </Box>
                    )}
                  {isHirist ? (
                    <>
                      {index % 5 == 0 && index > 0 && courses?.length > 0 ? (
                        <a
                          style={{textDecoration: 'none'}}
                          href={courses[(index / 5 - 1) % courses.length]?.link}
                          onClick={e => {
                            e.preventDefault();
                            setTimeout(() => {
                              const courseLink =
                                courses[(index / 5 - 1) % courses.length]?.link;
                              openUrl(courseLink, '_blank');
                              handleUbaTracking(
                                courses[(index / 5 - 1) % courses.length]?.id,
                                courses[(index / 5 - 1) % courses.length]?.title
                              );

                              handleTracking('lgClickNativeCourse', {
                                event_category: 'Learn and Grow',
                                Title:
                                  courses[(index / 5 - 1) % courses?.length]
                                    ?.title ?? '',
                                Origin: origin,
                                UserId: sessionStorage.getItem('userId') || '',
                                Device:
                                  sessionStorage.getItem('device') || 'desktop',
                                Status:
                                  sessionStorage.getItem('status') ||
                                  'LoggedOut',
                                Reforg:
                                  getQueryParamsAndSeekerId(
                                    courses[(index / 5 - 1) % courses.length]
                                      ?.link ?? ''
                                  ).queryParams['reforg'] || '',
                                RefInv:
                                  getQueryParamsAndSeekerId(
                                    courses[(index / 5 - 1) % courses.length]
                                      ?.link ?? ''
                                  ).queryParams['refinv'] || '',
                              });
                            }, 0);
                          }}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Box
                            display={'flex'}
                            sx={{
                              backgroundColor: 'white',
                              borderRadius: 1.5,
                              ml: '72px',
                              color: '#006ed7',
                              mt: 2,
                              alignItems: 'center',
                              py: 1,
                              mr: {xxs: 2, md: 4},
                              '&:hover': {
                                boxShadow: '0 5px 15px 0 rgba(0,0,0,.09)',
                              },
                            }}
                          >
                            <ImageWithFallback
                              className="joblist__logo"
                              src={
                                courses[(index / 5 - 1) % courses.length]
                                  .imageUrl ?? ''
                              }
                              fallback={''}
                              alt={`logo`}
                              width="50"
                              height={'50'}
                              itemId={index}
                              quality={50}
                              placeholder="blur"
                              blurDataURL={
                                courses[(index / 5 - 1) % courses.length]
                                  .imageUrl || ''
                              }
                            />
                            <Box
                              display={'flex'}
                              sx={{
                                justifyContent: 'space-between',
                                width: '100%',
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 600,
                                  width: {
                                    md: 'calc(100% - 150px)',
                                    xxs: 'calc(100% - 20px)',
                                  },
                                  ml: 2,
                                }}
                              >
                                {
                                  courses[(index / 5 - 1) % courses.length]
                                    .description
                                }
                              </Typography>
                              <Button
                                sx={{
                                  display: {xxs: 'none', md: 'block'},
                                  color: '#006ed7',
                                  border: '1px solid #006ed7',
                                  borderRadius: '18px',
                                  fontSize: 14,
                                  mr: 2,
                                  '&:hover': {
                                    color: '#006ed7',
                                    backgroundColor: 'white',
                                    border: '1px solid #006ed7',
                                    borderRadius: '18px',
                                    fontSize: 14,
                                  },
                                }}
                              >
                                {adObj?.ctaText || 'Know more'}
                              </Button>
                            </Box>
                          </Box>
                        </a>
                      ) : (
                        ''
                      )}
                      {(page === CATEGORY || page === TAG) &&
                      index == 8 &&
                      showPremium ? (
                        <DownAppBanner type={'EarlyApplicant'} />
                      ) : (
                        ''
                      )}
                      {window.location.href.includes('jobfeed') &&
                      index == 1 &&
                      jobFeedStore?.jobfeed?.length < 10 ? (
                        <Box
                          sx={{
                            backgroundColor: '#fef1e7',
                            padding: '16px',
                            marginLeft: '72px',
                            marginTop: '16px',
                            marginRight: {xxs: '16px', md: '32px'},
                            borderRadius: '12px',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 'bold',
                              color: '#e9630b',
                            }}
                          >
                            Hey {name}, unfortunately we don’t see many matching
                            jobs for your profile right now.
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 16,
                              color: '#e9630b',
                            }}
                          >
                            Please update your functional area, job feed tags,
                            experience, etc. to get more relevant opportunities!
                          </Typography>
                          <a
                            href={`/registration/addPersonalDetails`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Typography
                              sx={{
                                fontSize: 16,
                                fontWeight: 'bold',
                                color: '#e9630b',
                                textDecoration: 'underline',
                                textDecorationColor: '#e9630b',
                              }}
                            >
                              Update your profile now!
                            </Typography>
                          </a>
                        </Box>
                      ) : (
                        ''
                      )}
                      {categoryId && index == 3 && !isLarge ? (
                        <DownAppBanner type={'download'} />
                      ) : (
                        ''
                      )}
                      {((page == CATEGORY ||
                        page == TAG ||
                        page == JOBAPPLIED ||
                        page == JOBDETAIL ||
                        page == LOCATION_BASED) &&
                        index == 4 &&
                        device != 'mobile') ||
                      ((window.location.href.includes('/product-jobs') ||
                        window.location.href.includes('/ecommerce-jobs') ||
                        window.location.href.includes(
                          '/fintech-edtech-jobs'
                        )) &&
                        index == 1 &&
                        isLarge) ? (
                        <HiristSeoLinking
                          source={page == TAG ? 'taglist' : 'catlist'}
                        />
                      ) : (
                        ''
                      )}
                      {[11, 105, 306].includes(index) &&
                      !['jobfeed', 'recruiterJobfeed', 'search'].includes(
                        page
                      ) ? (
                        <>
                          <CityPageLisiting type="city" />
                        </>
                      ) : (
                        ''
                      )}
                      {((window.location.href.includes('/c/') ||
                        window.location.href.includes('/k/')) &&
                        [30, 60].includes(index)) ||
                      ((window.location.href.includes('/product-jobs') ||
                        window.location.href.includes('/ecommerce-jobs') ||
                        window.location.href.includes('/fintech-edtech-jobs') ||
                        page == COMPANY) &&
                        index == 5) ? (
                        <CityPageLisiting type="company" />
                      ) : (
                        ''
                      )}
                      {/* 
                      
                      
                      {seekerId &&
                      profile &&
                      jobFeed.length < 10 &&
                      [1].includes(index) &&
                      window.location.pathname.includes('/jobfeed') ? (
                        <UpdateProfileMessage profile={profile} />
                      ) : (
                        ''
                      )} */}
                    </>
                  ) : (
                    <></>
                  )}
                </Box>
              );
            })}
            {isHirist && loader && isLoading && (
              <Box
                sx={{width: '100%', display: 'flex', justifyContent: 'center'}}
              >
                <Loader />
              </Box>
            )}
          </ContainerComponent>
        ) : (
          <>
            {(filterApplied ||
              (isHirist && page == LOCATION_BASED) ||
              page === ENGINEERS_WEEK) &&
            (jobFeedStore?.isFilterLoaded ||
              (isHirist && page == LOCATION_BASED) ||
              page === ENGINEERS_WEEK) &&
            !jobFeedStore?.jobfeed?.length &&
            jobFeedStore.loadMoreEnabled !== null &&
            !isFilterApplied ? (
              page == LOCATION_BASED ? (
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    my: 1,
                    py: 2,
                    px: 2,
                    marginTop: isHirist ? 0 : {xxs: '-40px', md: '-25px'},
                  }}
                >
                  <Box data-nosnippet="true">
                    <Typography
                      sx={{
                        fontSize: isHirist ? '16px' : {xxs: '14px', md: '16px'},
                      }}
                    >
                      {`There are no jobs available from "${title?.split(
                        'Jobs in '
                      )[title?.split('Jobs in ').length - 1]}" However, you may
                    browse jobs from nearby ${title?.split('Jobs in ')[
                      title?.split('Jobs in ').length - 1
                    ]}`}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <NoJobsFound clearFilter={clearFilter} showBadge={showBadge} />
              )
            ) : (
              <Box
                sx={{
                  pt: 2.4,
                }}
              >
                {[...new Array(isHirist ? 20 : 50)].map(index => {
                  return (
                    <Box display="flex" key={`jobfeed-skel-${index}`}>
                      <Skeleton
                        variant="rounded"
                        height={60}
                        sx={{
                          width: {xxs: '75px', md: '65px'},
                          mt: {xxs: 0, md: 1},
                          ml: 2,
                          mb: 1,
                        }}
                      />
                      <Box display="block" width="100%">
                        <Skeleton
                          variant="rounded"
                          width={'50%'}
                          height={20}
                          sx={{
                            mt: {xxs: 0, md: 1},
                            ml: {xxs: 2, md: 'none'},
                            mb: 1,
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width={'90%'}
                          height={20}
                          sx={{
                            mt: {xxs: 0, md: 1},
                            ml: {xxs: 2, md: 'none'},
                            mb: 1,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>
      {!!jobFeedStore.loadMoreEnabled && !infiniteScroll && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            textAlign: 'center',
            mt: 5,
            justifyContent: 'center',
          }}
        >
          <LoadingButton
            loading={loadMoreLoading}
            variant="outlined"
            size="large"
            onClick={onLoadFeed}
            color="primary"
          >
            Load More
          </LoadingButton>
        </Box>
      )}
      {(checkJobs?.size || 0) + checkedJobs.size > 0 && (
        <Box
          display={'flex'}
          gap={2}
          alignItems={'center'}
          py={2.5}
          px={5}
          width={'100%'}
          sx={{background: '#000000bf'}}
          position={'fixed'}
          bottom={0}
          right={0}
          zIndex={100}
        >
          <Typography color={'#fff'} fontSize={18}>
            Selected{' '}
            {checkJobs ? checkJobs.size + checkedJobs.size : checkedJobs.size}
            /50
          </Typography>
          <Button
            sx={{
              py: 0.75,
              height: 35,
              width: 100,
              fontSize: 14,
              ':hover': {
                backgroundColor: 'primary.main',
                color: '#fff',
              },
            }}
            size="small"
            onClick={applyMultipleJobs}
            variant="outlined"
            color="primary"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {!load ? 'Apply All' : HiristLoader(getBackgroundColor(hover))}
          </Button>
          <Button
            sx={{
              py: 0.75,
              fontSize: 14,
              ':hover': {
                backgroundColor: 'primary.main',
                color: '#fff',
              },
            }}
            size="small"
            onClick={cancelApplyJobs}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
        </Box>
      )}
    </WrapperCard>
  );
};

export default JobFeed;
